import * as React from "react";
import * as ReactDOM from "react-dom";
import Cropper from "cropperjs";
import * as utils from "../../utils/utils";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../toolbar";
import { ChangeEvent } from "react";
import { Alert, AlertType } from "../../components/alert";

interface IProperties
{
    /**
     * Inline-Style.
     */
    style?: React.CSSProperties,

    /**
     * Das Bild welches verändert werden soll.
     */
    src?: string,

    /**
     * Angabe, ob ein Icon als Placeholder dargestellt werden soll. 
     */
    showPlaceholderIcon?: boolean,

    aspectRatio?: number,

    cropBoxWidth?: number,

    cropBoxHeight?: number,

    croppedImageWidth?: number,

    croppedImageHeight?: number,

    cropBoxResizable?: boolean,

    onSrcChanged?: (src?: string) => void
}

interface IState
{
    /**
     * Das Bild welches verändert werden soll.
     */
    src: string,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export class ImageCropper extends React.Component<IProperties, IState>
{
    private _imgRef: HTMLImageElement;
    private _inputFile: HTMLInputElement;
    private _cropper: Cropper;

    get FileType()
    {
        if (this._inputFile || this._inputFile.files || this._inputFile.files.length === 1)
            return this._inputFile.files[0].type;
    }

    public static defaultProps: Partial<IProperties> = {
        showPlaceholderIcon: true,
        aspectRatio: 4 / 3,
        cropBoxResizable: true
    };

    constructor(props: IProperties)
    {
        super(props);
        this.state = {
            src: props.src,
            alertType: AlertType.error
        };
    }

    private initCropper(src: string)
    {
        if (this._imgRef != null && src != null)
        {
            this._imgRef.setAttribute("src", src);
            this._cropper && this._cropper.destroy();
            this._cropper = new Cropper(this._imgRef, {
                aspectRatio: this.props.aspectRatio,
                viewMode: 1,
                responsive: true,
                minCropBoxHeight: this.props.cropBoxHeight || 0,
                minCropBoxWidth: this.props.cropBoxWidth || 0,
                cropBoxResizable: this.props.cropBoxResizable,
                ready: () =>
                {
                    if (this.props.cropBoxWidth > 0 && this.props.cropBoxHeight > 0)
                        this._cropper.setCropBoxData({
                            width: this.props.cropBoxWidth,
                            height: this.props.cropBoxHeight
                        });
                }
            });

            
        }
    }

    componentDidMount()
    {
        this.initCropper(this.state.src);
        this._inputFile.onchange = this.onFileInputChange;
    }

    /**
     * Destroy the cropper, this makes sure events such as resize are cleaned up and do not leak
     */
    componentWillUnmount()
    {
        if (this._imgRef)
        {
            delete this._imgRef;
        }

        if (this._cropper)
        {
            this._cropper.destroy();
            delete this._cropper;
        }
    }

    /**
     * Gibt das veränderte Bild als DataUrl zurück.
     */
    public crop(): string
    {
        var { croppedImageHeight, croppedImageWidth } = this.props;
        var canvas: HTMLCanvasElement;

        if (croppedImageHeight > 0 && croppedImageWidth > 0)
        {
            canvas = this._cropper.getCroppedCanvas({
                height: this.props.croppedImageHeight,
                width: this.props.croppedImageWidth
            });
        }
        else
        {
            canvas = this._cropper.getCroppedCanvas();
        }

        return canvas.toDataURL();
    }
    
    /**
     * Vergrößert oder verkleinert das Bild.
     * @param ratio
     */
    private onZoom(ratio: number): void
    {
        this._cropper.zoom(ratio);
    }

    /**
     * Bewegt das Bild in die angegebene Richtung.
     * @param direction Die Richtung in der das Bild bewegt werden soll.
     */
    private onMove(direction: "left" | "right" | "up" | "down"): void
    {
        switch (direction)
        {
            case "left":
                this._cropper.move(-10, 0);
                break;
            case "right":
                this._cropper.move(10, 0);
                break;
            case "up":
                this._cropper.move(0, -10);
                break;
            case "down":
                this._cropper.move(0, 10);
                break;
        }
    }

    /**
     * Rotiert das Bild in die angegeben Richtung um 45°.
     * @param direction Die Richtung in der das Bild rotiert werden soll.
     */
    private onRotate(direction: "left" | "right"): void
    {
        if (direction === "left")
            this._cropper.rotate(-45);
        else
            this._cropper.rotate(45);
    }

    /**
     * Setzt alle Änderungen am Bild zurück.
     */
    private onReset(): void
    {
        this._cropper.reset();
    }

    /**
     * Öffnet den Datei Dialog des Browsers.
     */
    private onUploadImage = () =>
    {
        this._inputFile.click();
    }

    /**
     * Wird ausgeführt, sobald eine Datei ausgewählt wurde.
     */
    private onFileInputChange = (e) =>
    {
        var input = e.target as any;
        if (input.files.length > 0)
        {
            var file: File = input.files[0];
            if (/^image\/\w+$/.test(file.type))
            {
                var uploadedImageURL = URL.createObjectURL(file);
                this.setState({
                    src: uploadedImageURL
                }, () =>
                    {
                        this.initCropper(uploadedImageURL);
                        this.props.onSrcChanged && this.props.onSrcChanged(uploadedImageURL);
                    });
            }
            else
            {
                this.setState({
                    alertMessage: "Es dürfen nur Bilder hochgeladen werden!"
                });
            }
        }
    }

    /**
     * Wird ausgeführt, sobald eine Datei in den drag n' drop Bereich gezogen wird.
     */
    private onDropFile = (e: React.DragEvent<HTMLDivElement>) =>
    {
        e.preventDefault();

        if (e.dataTransfer.items && e.dataTransfer.items.length === 1)
        {
            if (/^image\/\w+$/.test(e.dataTransfer.items[0].type))
            {
                var file = e.dataTransfer.items[0].getAsFile();
                var uploadedImageURL = URL.createObjectURL(file);
                this.setState({
                    src: uploadedImageURL
                }, () =>
                    {
                        this.initCropper(uploadedImageURL);
                        this.props.onSrcChanged && this.props.onSrcChanged(uploadedImageURL);
                    });
            }
            else
            {
                this.setState({
                    alertMessage: "Es dürfen nur Bilder hochgeladen werden!"
                });
            }
        }
        else
        {
            this.setState({
                alertMessage: "Es darf nur eine Datei hochgeladen werden!"
            });
        }

        e.dataTransfer.items && e.dataTransfer.items.clear();
    }

    /**
     * Bei einem "DragOver" die sSandardfunktion verhinden.
     */
    private onDragOverFile = (e: React.DragEvent<HTMLDivElement>) =>
    {
        e.preventDefault();
    }

    /**
     * Rendert die Drag n' Drop box.
     */
    private renderImageUpload(): JSX.Element
    {
        return (
            <div
                className="image-cropper_drop"
                onDrop={this.onDropFile}
                onDragOver={this.onDragOverFile}>
                {this.props.showPlaceholderIcon && <i className="image-cropper_image mdi mdi-image"></i>}
                <span>Bild hierher ziehen oder eines <a onClick={this.onUploadImage}>auswählen</a></span>
            </div>
        );
    }

    /**
     * Rendert den Image Cropper.
     */
    private renderCropper(): JSX.Element[]
    {
        const { src } = this.state;
        const cropper: JSX.Element[] = [];

        cropper.push(
            <div className="image-cropper_toolbar">
                <ToolBar
                    hasTopBorder={false}
                    hasBottomBorder={false}>
                    <ToolBarItem
                        icon="magnify-plus"
                        title="vergrößern"
                        onClick={() => this.onZoom(0.1)} />
                    <ToolBarItem
                        icon="magnify-minus"
                        title="verkleinern"
                        onClick={() => this.onZoom(-0.1)} />
                    <ToolBarSeparator />
                    <ToolBarItem
                        icon="arrow-left-thick"
                        onClick={() => this.onMove("left")} />
                    <ToolBarItem
                        icon="arrow-right-thick"
                        onClick={() => this.onMove("right")} />
                    <ToolBarItem
                        icon="arrow-up-thick"
                        onClick={() => this.onMove("up")} />
                    <ToolBarItem
                        icon="arrow-down-thick"
                        onClick={() => this.onMove("down")} />
                    <ToolBarSeparator />
                    <ToolBarItem
                        icon="rotate-left"
                        onClick={() => this.onRotate("left")} />
                    <ToolBarItem
                        icon="rotate-right"
                        onClick={() => this.onRotate("right")} />
                    <ToolBarSeparator />
                    <ToolBarItem
                        icon="refresh"
                        title="Änderungen zurücksetzen"
                        isDisabled={!this.props.cropBoxResizable}
                        onClick={() => this.onReset()} />
                    <ToolBarSeparator />
                    <ToolBarItem
                        icon="upload"
                        text="Anderes Bild auswählen"
                        onClick={this.onUploadImage} />
                </ToolBar>
            </div>
        );

        cropper.push(
            <div className="image-cropper_canvas">
                <img
                    ref={(ref) => this._imgRef = ref}
                    src={src}
                    style={{
                        maxWidth: "100%",
                        display: "none"
                    }} />
            </div>
        );

        return cropper;
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { style } = this.props;
        const { src, alertMessage, alertType } = this.state;

        return (
            <div
                className="image-cropper"
                style={style}>
                <input
                    ref={(ref) => this._inputFile = ref}
                    type="file"
                    accept="image/*"
                    style={{
                        display: "none"
                    }} />
                <Alert
                    style={utils.getGridChildStyle(1, 1, 1, 1)}
                    alertType={alertType}
                    isVisible={alertMessage ? true : false}
                    isClosable={true}
                    onHide={() => this.setState({ alertMessage: null })}>
                    {alertMessage}
                </Alert>
                {src == null && this.renderImageUpload()}
                {src != null && this.renderCropper()}
            </div>
        );
    }
}